import React, { Component } from 'react'
import { Alert, Button, Card, Col, Collapse, Input, Row, Spin, Tag, Typography, message } from 'antd'
import {
    ApiOutlined,
    AppleOutlined,
    CheckCircleOutlined,
    CloudDownloadOutlined,
    CopyOutlined,
    LoginOutlined,
    QuestionCircleOutlined,
    RocketOutlined,
    SendOutlined,
    SettingOutlined,
    WindowsOutlined,
} from '@ant-design/icons'
import { getA2aPortalOverview } from '../../request'
import codexPlusToolboxGuide from './assets/codexplus-toolbox-guide.png'
import macosGatekeeperStillOpen from './assets/macos-gatekeeper-still-open.jpg'

const DOWNLOADS = [
    {
        key: 'macos-arm64',
        icon: <AppleOutlined />,
        platform: 'macOS · Apple 芯片',
        detail: '适用于 M1、M2、M3、M4 等 Apple Silicon Mac',
    },
    {
        key: 'macos-x64',
        icon: <AppleOutlined />,
        platform: 'macOS · Intel 芯片',
        detail: '适用于使用 Intel 处理器的 Mac',
    },
    {
        key: 'windows-x64',
        icon: <WindowsOutlined />,
        platform: 'Windows · 64 位',
        detail: '适用于公司常用的 Windows 10 / 11 电脑',
    },
]

const { Panel } = Collapse
const { Text } = Typography
const MACOS_QUARANTINE_COMMAND = 'xattr -dr com.apple.quarantine "/Applications/CodexPlus.app"'

class A2ATab extends Component {
    state = {
        loading: true,
        portalOverview: null,
        portalLoadFailed: false,
    }

    componentDidMount() {
        this.loadPortalOverview()
    }

    loadPortalOverview = async () => {
        this.setState({ loading: true, portalLoadFailed: false })
        try {
            const portalOverview = await getA2aPortalOverview()
            this.setState({ loading: false, portalOverview: portalOverview || {} })
        } catch (error) {
            // 2026-08-08 coder(lq): 发布中心不可用时明确显示失败，不回退到过期或虚构下载地址。
            this.setState({ loading: false, portalOverview: null, portalLoadFailed: true })
        }
    }

    copyPrompt = () => {
        const prompt = this.state.portalOverview?.prompt?.promptContent || ''
        if (!prompt) {
            message.warning('管理员尚未发布任务发送方 Prompt')
            return
        }
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(prompt)
                .then(() => message.success('任务发送方 Prompt 已复制'))
                .catch(() => message.warning('复制失败，请手动选择并复制'))
            return
        }
        message.warning('当前浏览器不支持自动复制，请手动选择并复制')
    }

    downloadAsset = asset => {
        if (!asset?.url) {
            message.warning('该平台安装包尚未发布')
            return
        }
        window.open(asset.url, '_blank')
    }

    scrollToDownloads = () => {
        document.getElementById('codexplus-downloads')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    render() {
        const { loading, portalOverview, portalLoadFailed } = this.state
        const senderPrompt = portalOverview?.prompt?.promptContent || ''
        const legacyRelease = portalOverview?.release || null
        const apiPlatformReleases = portalOverview?.platformReleases || {}
        // 2026-08-08 coder(lq): 支持前后端分批发布；旧接口期间仍可展示整体版本已有的安装包。
        const platformReleases = Object.keys(apiPlatformReleases).length > 0
            ? apiPlatformReleases
            : DOWNLOADS.reduce((result, download) => {
                const hasAsset = legacyRelease?.assets?.some(asset => asset.platform === download.key)
                if (hasAsset) result[download.key] = legacyRelease
                return result
            }, {})
        const hasAvailableRelease = DOWNLOADS.some(download => platformReleases[download.key])

        return (
            <div className="profile-a2a-wrap">
                <section className="profile-a2a-hero">
                    <div className="profile-a2a-hero-icon"><SendOutlined /></div>
                    <div>
                        <Tag color="blue">Agent to Agent</Tag>
                        <h1>把 Codex 里的任务，直接派给另一位同事的 Codex</h1>
                        <p>
                            A2A 是公司的 Codex 任务协作功能。发送方不用再复制任务到聊天工具，接收方也不用手工整理任务信息：
                            任务标题、内容、所属项目和执行人会一起送达 CodexPlus，确认后创建成原生 Codex 会话，完成状态还能自动回传。
                        </p>
                    </div>
                </section>

                <Card bordered={false} className="profile-a2a-section-card profile-a2a-guide-card">
                    <div className="profile-a2a-section-head">
                        <div>
                            <h2><RocketOutlined /> 第一次怎么用</h2>
                            <p>第一次使用请从第 1 步开始；前三步只需配置一次，以后日常收发任务直接从第 4 步开始。</p>
                        </div>
                    </div>
                    {/* 2026-08-09 coder(lq): 门户只解释操作路径，统一 Prompt 由 CodexPlus 自动读取，避免用户重复手工配置。 */}
                    <div className="profile-a2a-steps">
                        <div className="profile-a2a-step">
                            <span className="profile-a2a-step-number">1</span>
                            <div className="profile-a2a-step-icon"><CloudDownloadOutlined /></div>
                            <div className="profile-a2a-step-main">
                                <div className="profile-a2a-step-title"><strong>下载并安装 CodexPlus</strong><Tag>首次一次</Tag></div>
                                <p>在本页下方选择与你电脑匹配的 macOS 或 Windows 安装包。安装完成后，从 CodexPlus 启动官方 Codex。</p>
                                <small>完成标志：Codex 页面右上方出现 CodexPlus 工具箱按钮。</small>
                                {/* 2026-08-09 coder(lq): 用实际界面截图帮助首次使用者识别 CodexPlus 工具箱入口。 */}
                                <button
                                    type="button"
                                    className="profile-a2a-toolbox-guide"
                                    onClick={() => window.open(codexPlusToolboxGuide, '_blank')}
                                    title="点击查看原图"
                                >
                                    <img src={codexPlusToolboxGuide} alt="Codex 页面右上方的 CodexPlus 四宫格工具箱图标" />
                                    <span>请在 Codex 页面右上方找到箭头所指的四宫格图标；窗口宽度不同时，位置可能略有变化。点击图片可查看原图。</span>
                                </button>
                            </div>
                        </div>
                        <div className="profile-a2a-step">
                            <span className="profile-a2a-step-number">2</span>
                            <div className="profile-a2a-step-icon"><LoginOutlined /></div>
                            <div className="profile-a2a-step-main">
                                <div className="profile-a2a-step-title"><strong>登录公司账号</strong><Tag>首次一次</Tag></div>
                                <p>使用乐纯员工账号或钉钉登录。登录成功后 CodexPlus 会自动保存 API Key，不需要手工复制或设置环境变量。</p>
                                <small>完成标志：CodexPlus A2A 引导中的“登录状态”显示已完成。</small>
                            </div>
                        </div>
                        <div className="profile-a2a-step">
                            <span className="profile-a2a-step-number">3</span>
                            <div className="profile-a2a-step-icon"><SettingOutlined /></div>
                            <div className="profile-a2a-step-main">
                                <div className="profile-a2a-step-title"><strong>在 CodexPlus 中启用 A2A</strong><Tag>首次一次</Tag></div>
                                <p>打开“CodexPlus → A2A”，先设置 A2A 主目录，再点击“lechun-a2a MCP”旁的“开始配置”。CodexPlus 会自动读取管理员发布的最新 Prompt，创建配置会话并发送给 Codex。</p>
                                <small>完成标志：登录状态、A2A 主目录和 lechun-a2a MCP 三项检查全部通过，任务列表自动显示。</small>
                            </div>
                        </div>
                        <div className="profile-a2a-step">
                            <span className="profile-a2a-step-number">4</span>
                            <div className="profile-a2a-step-icon"><SendOutlined /></div>
                            <div className="profile-a2a-step-main">
                                <div className="profile-a2a-step-title"><strong>发送或接收任务</strong><Tag color="blue">日常使用</Tag></div>
                                <p>发送方在 Codex 中说明任务，让 lechun-a2a MCP 选择执行人并在发送前确认；接收方在 CodexPlus 的“我执行的”中核查任务，选择本机 Codex 项目后创建会话。</p>
                                <small>任务不会因为服务端项目名称而覆盖本机 Codex 项目，两者通过项目映射关联。</small>
                            </div>
                        </div>
                        <div className="profile-a2a-step">
                            <span className="profile-a2a-step-number">5</span>
                            <div className="profile-a2a-step-icon"><CheckCircleOutlined /></div>
                            <div className="profile-a2a-step-main">
                                <div className="profile-a2a-step-title"><strong>核查结果并确认完成</strong><Tag color="blue">日常使用</Tag></div>
                                <p>Codex 完成开发后，由执行人检查结果。确认没有问题，再回到任务卡片点击“确认完成”，服务端和发送方会同步收到完成状态。</p>
                                <small>请不要仅因为 Codex 回复结束就直接确认完成，仍需人工检查实际结果。</small>
                            </div>
                        </div>
                    </div>
                </Card>

                <Card bordered={false} className="profile-a2a-section-card">
                    <div className="profile-a2a-section-head">
                        <div>
                            <h2><ApiOutlined /> A2A 配置内容由 CodexPlus 自动获取</h2>
                            <p>不需要在这里复制 Prompt。请在 CodexPlus 的 A2A 引导中点击“开始配置”，CodexPlus 会读取管理员最新保存的内容并发送给 Codex。</p>
                        </div>
                        <div className="profile-a2a-section-actions">
                            <Button icon={<CloudDownloadOutlined />} type="primary" onClick={this.scrollToDownloads}>下载 CodexPlus</Button>
                            {portalLoadFailed && <Button onClick={this.loadPortalOverview}>重试</Button>}
                        </div>
                    </div>
                    <Alert
                        showIcon
                        type="success"
                        message="推荐方式：在 CodexPlus 中一键配置"
                        description="CodexPlus 会展示配置内容和风险提示，只有你阅读并勾选确认后才会发送给 Codex；检测到 lechun-a2a MCP 后才会显示配置成功。"
                    />
                    <details className="profile-a2a-manual-prompt">
                        <summary>排障备用：查看并手工复制配置 Prompt</summary>
                        <p>仅当技术人员要求手工排查时使用。正常情况下请不要重复执行，以免产生重复配置。</p>
                        <Spin spinning={loading} tip="正在加载配置 Prompt">
                            <Input.TextArea
                                className="profile-a2a-prompt"
                                readOnly
                                value={senderPrompt}
                                autoSize={{ minRows: 8, maxRows: 16 }}
                                placeholder={portalLoadFailed
                                    ? '配置 Prompt 加载失败，请点击“重试”重新加载。'
                                    : '管理员尚未发布配置 Prompt。'}
                            />
                        </Spin>
                        <Button icon={<CopyOutlined />} disabled={!senderPrompt} onClick={this.copyPrompt}>复制备用 Prompt</Button>
                    </details>
                </Card>

                <Card id="codexplus-downloads" bordered={false} className="profile-a2a-section-card">
                    <div className="profile-a2a-section-head">
                        <div>
                            <h2><RocketOutlined /> CodexPlus 是什么</h2>
                            <p>CodexPlus 是公司内部的 Codex 桌面增强工具。它不替代 Codex，而是把 A2A 和常用效率能力直接放进 Codex 页面。</p>
                        </div>
                    </div>
                    <div className="profile-a2a-capabilities">
                        <span>实时接收 A2A 任务</span>
                        <span>创建原生 Codex 会话</span>
                        <span>项目映射与任务看板</span>
                        <span>会话管理与备份</span>
                        <span>自定义快捷按钮</span>
                        <span>macOS / Windows 支持</span>
                    </div>
                    <Alert
                        showIcon
                        type="info"
                        message="首次使用提示"
                        description="安装后请完全退出官方 Codex，再启动 CodexPlus。CodexPlus 会启动 Codex 并把增强功能加载到页面中；任务不会自动执行，除非你自己在设置里开启了“收到任务后直接执行”。"
                    />
                </Card>

                <Card bordered={false} className="profile-a2a-section-card">
                    <div className="profile-a2a-section-head">
                        <div>
                            <h2><CloudDownloadOutlined /> 下载 CodexPlus</h2>
                            <p>{hasAvailableRelease ? '各平台独立展示已发布的最新版本，请选择自己电脑对应的安装包。' : '管理员尚未发布 CodexPlus 稳定版。'}</p>
                        </div>
                        <Button icon={<CloudDownloadOutlined />} onClick={this.loadPortalOverview}>刷新版本</Button>
                    </div>
                    {portalLoadFailed && <Alert showIcon type="error" message="CodexPlus 版本信息加载失败" description="请检查网络后重试。" />}
                    {!portalLoadFailed && !hasAvailableRelease && <Alert showIcon type="info" message="暂无可下载版本" description="请联系管理员在 A2A 管理页发布版本。" />}
                    {hasAvailableRelease && <Row gutter={[16, 16]}>
                        {DOWNLOADS.map(download => {
                            const platformRelease = platformReleases[download.key]
                            const asset = platformRelease?.assets?.find(item => item.platform === download.key)
                            return (
                            <Col xs={24} lg={8} key={download.key}>
                                <div className="profile-a2a-download-card">
                                    <div className="profile-a2a-download-icon">{download.icon}</div>
                                    <div className="profile-a2a-download-main">
                                        <strong>{download.platform}</strong>
                                        <p>{download.detail}</p>
                                        <Tag className="profile-a2a-download-version" color={platformRelease ? 'blue' : 'default'}>
                                            {platformRelease ? `当前版本 ${platformRelease.version}` : '暂无版本'}
                                        </Tag>
                                        <small>{asset ? `${asset.name} · SHA-256 ${asset.sha256 || '-'}` : '该平台暂未发布安装包'}</small>
                                    </div>
                                    <Button type="primary" disabled={!asset} icon={<CloudDownloadOutlined />} onClick={() => this.downloadAsset(asset)}>
                                        {asset ? '下载安装包' : '暂不可用'}
                                    </Button>
                                </div>
                            </Col>
                            )
                        })}
                    </Row>}
                </Card>

                <Card bordered={false} className="profile-a2a-section-card profile-a2a-faq-card">
                    <div className="profile-a2a-section-head">
                        <div>
                            <h2><QuestionCircleOutlined /> 常见问题</h2>
                            <p>下载安装或使用过程中遇到问题，可以先按下面的方法处理；仍未解决时再把 CodexPlus 诊断日志发给技术人员。</p>
                        </div>
                    </div>
                    {/* 2026-08-10 coder(lq): 把高频安装与接入问题集中在下载页，降低首次使用者的排障成本。 */}
                    <Collapse bordered={false} className="profile-a2a-faq" expandIconPosition="right">
                        <Panel header="macOS 提示“应用已损坏”或“无法打开”，怎么办？" key="macos-quarantine">
                            {/* 2026-08-11 coder(lq): 优先提供 macOS 图形化放行步骤，减少普通用户执行终端命令的门槛。 */}
                            <p>请先确认安装包来自公司 A2A 页面。若 macOS 阻止打开 CodexPlus，请打开<strong>系统设置 → 隐私与安全性</strong>，向下滚动到“安全性”部分，在“已阻止 CodexPlus 以保护 Mac”旁点击<strong>仍要打开</strong>，然后按系统提示确认。</p>
                            <img className="profile-a2a-gatekeeper-guide" src={macosGatekeeperStillOpen} alt="macOS 隐私与安全性页面中的仍要打开按钮" />
                            <p>如果没有看到“仍要打开”，请先尝试启动一次 CodexPlus，再立即回到“隐私与安全性”页面查看。</p>
                            <p>如果系统设置方式仍无法解决，可打开 macOS 的“终端”，复制并执行下面的命令，然后重新打开 CodexPlus：</p>
                            <div className="profile-a2a-command">
                                <Text code copyable={{ text: MACOS_QUARANTINE_COMMAND }}>{MACOS_QUARANTINE_COMMAND}</Text>
                            </div>
                            <p className="profile-a2a-faq-tip">这条命令只会清除 CodexPlus 的 macOS 隔离标记，不会删除应用或个人数据。</p>
                        </Panel>
                        <Panel header="Mac 应该下载 Apple 芯片版还是 Intel 版？" key="macos-architecture">
                            <p>点击屏幕左上角苹果菜单，选择“关于本机”。芯片显示 M1、M2、M3、M4 或更新型号时，下载“macOS · Apple 芯片”；处理器显示 Intel 时，下载“macOS · Intel 芯片”。</p>
                        </Panel>
                        <Panel header="安装后，Codex 页面里没有看到 CodexPlus 图标？" key="toolbox-missing">
                            <p>请先完全退出官方 Codex 和 CodexPlus，再从“应用程序”中启动 CodexPlus，让它自动打开 Codex。进入 Codex 后，在页面右上方查找四宫格工具箱图标；如果仍未出现，请在 CodexPlus 设置中导出诊断日志。</p>
                        </Panel>
                        <Panel header="更新安装时提示 CodexPlus 正在使用，怎么办？" key="app-in-use">
                            <p>先退出 CodexPlus，并确认菜单栏和程序坞中都没有正在运行的 CodexPlus，再重新安装。若仍提示正在使用，请同时退出官方 Codex 后重试。</p>
                        </Panel>
                        <Panel header="A2A 配置完成后，为什么仍看不到任务？" key="a2a-task-missing">
                            <p>请依次确认登录状态、A2A 主目录和 lechun-a2a MCP 三项检查都已通过，再检查 A2A 页面顶部的服务连接状态。任务没有映射或选择本机 Codex 项目时，不会自动创建会话，但仍应保留在任务列表中等待处理。</p>
                        </Panel>
                    </Collapse>
                </Card>
            </div>
        )
    }
}

export default A2ATab
